import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X, FileText, ArrowRight, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import robustFetch from "@/utils/robustFetch";

interface SearchResult {
  type: "format" | "conversion" | "category" | "view-all";
  title: string;
  description: string;
  url: string;
  badge?: string;
}

export default function GlobalSearch() {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handle click outside to close
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
        setSearchTerm("");
        setResults([]);
      }
    }

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timeoutId = setTimeout(async () => {
      try {
        const response = await robustFetch(
          `/api/search/global?q=${encodeURIComponent(searchTerm)}`,
          {
            timeout: 5000,
            retries: 1,
          },
        );

        if (response.ok) {
          const data = await response.json();
          setResults(data.results || []);
        } else {
          setResults([]);
        }
      } catch (error) {
        console.error("Search error:", error);
        setResults([]);
      } finally {
        setIsLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const handleResultClick = () => {
    setIsOpen(false);
    setSearchTerm("");
    setResults([]);
  };

  const getResultIcon = (type: string) => {
    switch (type) {
      case "format":
        return "📄";
      case "conversion":
        return "🔄";
      case "category":
        return "📁";
      case "view-all":
        return "📋";
      default:
        return "📌";
    }
  };

  const getResultBadgeColor = (type: string) => {
    switch (type) {
      case "format":
        return "bg-blue-100 text-blue-700";
      case "conversion":
        return "bg-green-100 text-green-700";
      case "category":
        return "bg-purple-100 text-purple-700";
      case "view-all":
        return "bg-orange-100 text-orange-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="relative" ref={searchRef}>
      {/* Search Icon Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="text-white hover:text-black hover:bg-white relative"
        aria-label="Search"
      >
        <Search className="h-5 w-5" />
      </Button>

      {/* Search Overlay */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 bg-black/50 z-40" />

          {/* Search Panel */}
          <div className="fixed top-16 left-1/2 -translate-x-1/2 w-full max-w-2xl z-50 px-4">
            <div className="bg-white rounded-lg shadow-2xl border border-gray-300 overflow-hidden">
              {/* Search Input */}
              <div className="p-5 border-b border-gray-200 bg-white">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-6 h-6" />
                  <Input
                    ref={inputRef}
                    type="text"
                    placeholder="Search formats, conversions, categories..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-12 pr-12 h-14 text-lg text-gray-900 placeholder:text-gray-500 border-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-0 bg-white"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => {
                        setSearchTerm("");
                        setResults([]);
                      }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  )}
                </div>
              </div>

              {/* Search Results */}
              <div className="max-h-[400px] overflow-y-auto">
                {isLoading && (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                    <span className="ml-2 text-base text-gray-700 font-medium">
                      Searching...
                    </span>
                  </div>
                )}

                {!isLoading && searchTerm.length < 2 && (
                  <div className="p-8 text-center">
                    <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-base text-gray-700 font-medium">
                      Type at least 2 characters to search
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      Search for formats (PDF, JPEG), conversions (PDF to DOCX),
                      or categories (image converter)
                    </p>
                  </div>
                )}

                {!isLoading &&
                  searchTerm.length >= 2 &&
                  results.length === 0 && (
                    <div className="p-8 text-center">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p className="text-base text-gray-700 font-medium">
                        No results found for "{searchTerm}"
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        Try searching for a format, conversion, or category
                      </p>
                    </div>
                  )}

                {!isLoading && results.length > 0 && (
                  <div className="divide-y divide-gray-100">
                    {results.map((result, index) => (
                      <Link
                        key={index}
                        to={result.url}
                        onClick={handleResultClick}
                        className={cn(
                          "block p-4 hover:bg-gray-50 transition-colors group",
                          result.type === "view-all" &&
                            "bg-orange-50/50 hover:bg-orange-100/50 border-t-2 border-orange-200",
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-2xl mt-0.5">
                            {getResultIcon(result.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-semibold text-base text-gray-900 group-hover:text-primary transition-colors">
                                {result.title}
                              </h4>
                              {result.badge && (
                                <Badge
                                  variant="secondary"
                                  className={cn(
                                    "text-xs font-medium",
                                    getResultBadgeColor(result.type),
                                  )}
                                >
                                  {result.badge}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 line-clamp-1">
                              {result.description}
                            </p>
                          </div>
                          <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Footer Hint */}
              {results.length > 0 && (
                <div className="p-3 bg-gray-50 border-t border-gray-200 text-center">
                  <p className="text-sm text-gray-600">
                    Press{" "}
                    <kbd className="px-2 py-1 bg-white border border-gray-300 rounded text-xs font-semibold text-gray-700">
                      ESC
                    </kbd>{" "}
                    to close
                  </p>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
